import { useEffect, useRef, useState } from "react";
import type { LeadValues } from "../../shared/lead";
import { clarityEvent, clarityTag } from "./clarity";
import { buildLeadPayload } from "./lead-payload";
import { submitLead, type SubmitOutcome } from "./submit-lead";

export type LeadPhase =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "failed"; retryUsed: boolean };

/** A key per distinct body: a resend of the same values is deduplicated, an edit gets a new row. */
function newIdempotencyKey(): string {
  const id =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `qurtuba-form-${id}`;
}

/** Keep the spinner on screen long enough that a fast answer does not flash it away. */
const MIN_SUBMITTING_MS = 400;

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function failureDetail(outcome: Extract<SubmitOutcome, { ok: false }>) {
  if (outcome.reason === "http") {
    return `${outcome.status}${outcome.code ? ` ${outcome.code}` : ""}${outcome.detail ? ` ${outcome.detail}` : ""}`;
  }
  if (outcome.reason === "config")
    return "VITE_INTAKE_URL or VITE_INTAKE_KEY is empty in the build";
  return outcome.detail;
}

/**
 * Owns the submit phase, the payload and the one retry. The form keeps the fields and the
 * validation; this hook is the only place that talks to the intake endpoint.
 */
export function useLeadFlow(onSuccess: () => void) {
  const [phase, setPhase] = useState<LeadPhase>({ kind: "idle" });
  const success = useRef(onSuccess);
  const pending = useRef<LeadValues | null>(null);
  const sent = useRef<{ body: string; key: string } | null>(null);
  const inFlight = useRef(false);

  useEffect(() => {
    success.current = onSuccess;
  }, [onSuccess]);

  async function send(values: LeadValues, isRetry: boolean) {
    const payload = buildLeadPayload(values);
    const body = JSON.stringify(payload);
    if (!sent.current || sent.current.body !== body) {
      sent.current = { body, key: newIdempotencyKey() };
    }

    const url = import.meta.env.VITE_INTAKE_URL ?? "";
    const key = import.meta.env.VITE_INTAKE_KEY ?? "";
    const started = Date.now();
    const outcome = await submitLead({
      url,
      key,
      idempotencyKey: sent.current.key,
      payload,
      retries: 0,
    });

    const remaining = MIN_SUBMITTING_MS - (Date.now() - started);
    if (remaining > 0) await wait(remaining);

    if (outcome.ok) {
      clarityEvent("lead_submitted");
      success.current();
      return;
    }

    const detail = failureDetail(outcome);
    console.error("[qurtuba] lead submit failed", {
      reason: outcome.reason,
      status: outcome.reason === "http" ? outcome.status : null,
      code: outcome.reason === "http" ? outcome.code : null,
      detail,
      url: url === "" ? "(empty)" : url,
      keySet: key !== "",
    });
    clarityTag(
      "lead_failure",
      outcome.reason === "http" ? (outcome.code ?? `http_${outcome.status}`) : outcome.reason,
    );
    clarityTag("lead_failure_detail", detail);
    clarityEvent("lead_failed");
    setPhase({ kind: "failed", retryUsed: isRetry });
  }

  function onFailure(attempt: boolean, error: unknown) {
    console.error("[qurtuba] lead submit crashed", error);
    setPhase({ kind: "failed", retryUsed: attempt });
  }

  function submit(values: LeadValues) {
    if (inFlight.current) return;
    pending.current = values;
    inFlight.current = true;
    setPhase({ kind: "submitting" });
    void send(values, false)
      .catch((error: unknown) => onFailure(false, error))
      .finally(() => {
        inFlight.current = false;
      });
  }

  function retry() {
    if (inFlight.current || phase.kind !== "failed" || phase.retryUsed || !pending.current) return;
    inFlight.current = true;
    clarityEvent("lead_retried");
    setPhase({ kind: "submitting" });
    void send(pending.current, true)
      .catch((error: unknown) => onFailure(true, error))
      .finally(() => {
        inFlight.current = false;
      });
  }

  function reset() {
    if (inFlight.current) return;
    pending.current = null;
    setPhase({ kind: "idle" });
  }

  return {
    phase,
    submit,
    retry,
    reset,
    bypass: () => success.current(),
  };
}
